import styled from "@emotion/styled";
import type { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import { darkGrey, lightGreen, lightGrey, transparentWhite } from "@stepfinance/react";
import { Select, Switch } from "antd";
import React, { useCallback } from "react";

import { useAppConfig, useNetworkConfig } from "../../contexts/app/config";
import { displayLanguageCodesMap } from "../../contexts/language/imports";
import { useLanguageContext } from "../../contexts/language/language";
import { useMarkets } from "../../contexts/market";
import { STEP_NETWORK_CONFIG_MAP } from "../../utils/network";
import { load as loadTokenInfo } from "../../utils/tokenInfo";
import { StyledSelect } from "../Select/Select";

const NETWORKS = Object.entries(STEP_NETWORK_CONFIG_MAP);

export const SettingsMenu: React.FC = () => {
  const { network, setNetwork } = useNetworkConfig();
  const { hideDust, setHideDust, hideUnknownTokens, setHideUnknownTokens, hideCompressedNFTs, setHideCompressedNFTs } =
    useAppConfig();
  const { displayCurrency, setDisplayCurrency, displayCurrencies } = useMarkets();
  const { setChosenLanguage, languageSetting, currentLanguageData } = useLanguageContext();
  const currentSelectedLanguage = displayLanguageCodesMap[languageSetting];

  const {
    pages: {
      allPages: {
        topBar: { userSettings: userSettingsText },
      },
    },
  } = currentLanguageData;

  const updateNetwork = useCallback(
    (val: WalletAdapterNetwork) => {
      void loadTokenInfo(val).finally(() => setNetwork(val));
    },
    [setNetwork],
  );

  return (
    <StyledSettingsMenu>
      <div className="toggle-wrapper">
        <span>{userSettingsText.hideSmallBalance}</span>
        <StepSwitch defaultChecked={hideDust} onChange={setHideDust} size="small" />
      </div>
      <div className="toggle-wrapper">
        <span>{userSettingsText.hideUnknownTokens}</span>
        <StepSwitch defaultChecked={hideUnknownTokens} onChange={setHideUnknownTokens} size="small" />
      </div>
      <div className="toggle-wrapper">
        <span>{userSettingsText.hideCompressedNfts}</span>
        <StepSwitch defaultChecked={hideCompressedNFTs} onChange={setHideCompressedNFTs} size="small" />
      </div>

      <DisplayLabelAndSelect>
        <div>{userSettingsText.displayCurrency} </div>
        <StyledSelect<string> onSelect={setDisplayCurrency} value={displayCurrency}>
          {displayCurrencies.map((symbol) => (
            <Select.Option value={symbol} key={symbol}>
              {symbol}
            </Select.Option>
          ))}
        </StyledSelect>
      </DisplayLabelAndSelect>

      <DisplayLabelAndSelect>
        {userSettingsText.displayLanguage}
        <StyledSelect<string> value={currentSelectedLanguage} onSelect={setChosenLanguage}>
          {Object.values(displayLanguageCodesMap).map((languageCode) => (
            <Select.Option value={languageCode} key={languageCode}>
              {languageCode}
            </Select.Option>
          ))}
        </StyledSelect>
      </DisplayLabelAndSelect>

      <DisplayLabelAndSelect>
        {userSettingsText.solanaNetwork}
        <StyledSelect onSelect={updateNetwork} value={network}>
          {NETWORKS.map(([networkID, network]) => (
            <Select.Option value={networkID} key={networkID}>
              {network.name}
            </Select.Option>
          ))}
        </StyledSelect>
      </DisplayLabelAndSelect>
    </StyledSettingsMenu>
  );
};

export const appBarMenuPopoverStyles = `
  padding: 10px;

  background: ${darkGrey};
  border-radius: 8px;
`;

const StyledSettingsMenu = styled.div`
  ${appBarMenuPopoverStyles}

  border-radius: 8px;
  color: ${transparentWhite};
  display: flex;
  flex-direction: column;
  font-size: 10px;
  gap: 5px;
  width: 18em;

  .toggle-wrapper {
    display: flex;
    justify-content: space-between;
  }
`;

const StepSwitch = styled(Switch)`
  button {
    background: ${(props) => (props.defaultChecked ? lightGreen : darkGrey)};
  }
`;

const DisplayLabelAndSelect = styled.div`
  .ant-select {
    width: 100%;
    .ant-select-selector {
      background: #141414;
      border-radius: 2px;

      padding: 8px 12px;
      border: none;
      font-size: 14px;
      color: ${lightGrey};

      .ant-select-selection-item {
        line-height: 14px;
      }
    }
    .ant-select-arrow {
      color: ${lightGrey};
      .anticon-down svg path {
        stroke: ${lightGrey};
        stroke-width: 50;
      }
    }
  }
`;
